'use client';

import type { Exploit } from '@revoke.cash/core/exploits';
import Loader from 'components/common/Loader';
import PremiumChainStatusSection from 'components/common/PremiumChainStatusSection';
import { usePremiumExploitChecker } from 'lib/hooks/ethereum/usePremiumExploitChecker';
import { useAddress } from 'lib/hooks/page-context/AddressIdentityContext';
import ExploitResultSection from './ExploitResultSection';
import ExploitSafeMessage from './ExploitSafeMessage';

interface Props {
  exploits: Exploit[];
}

const PremiumExploitDashboard = ({ exploits }: Props) => {
  const { address } = useAddress();
  const { results, chainStatuses } = usePremiumExploitChecker(exploits);

  const affectedResults = results.filter((r) => r.status === 'affected');
  const previouslyAffectedResults = results.filter((r) => r.status === 'previously_affected');

  const finishedLoading = chainStatuses.every((chain) => chain.status !== 'loading');
  const hasErrors = chainStatuses.some((chain) => chain.status === 'error');
  const hasResults = affectedResults.length > 0 || previouslyAffectedResults.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <PremiumChainStatusSection chainStatuses={chainStatuses} />

      {!finishedLoading && <Loader isLoading className="h-40" />}
      {finishedLoading && !hasResults && <ExploitSafeMessage hasErrors={hasErrors} />}
      {affectedResults.length > 0 && (
        <ExploitResultSection status="affected" results={affectedResults} address={address} />
      )}
      {previouslyAffectedResults.length > 0 && (
        <ExploitResultSection status="previously_affected" results={previouslyAffectedResults} address={address} />
      )}
    </div>
  );
};

export default PremiumExploitDashboard;
